/** Section 3 — Gates G1–G11: checklists a case must satisfy to move on. Gate keys are code; checklist items are Emma's. */
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { LiveBadge, SectionIntro } from "./processShared";

const ENFORCED = ["G1", "G2", "G4", "G5"];

export default function ProcessGatesSection({ body, setBody }) {
  const gates = body.gates || [];
  const updateGate = (key, fn) => setBody((b) => ({ ...b, gates: b.gates.map((g) => (g.key === key ? fn(g) : g)) }));
  const addItem = (key) => updateGate(key, (g) => ({ ...g, items: [...(g.items || []), { id: `at_${Date.now().toString(36)}`, label: "", kind: "attested", enabled: true, disabled_reason: "" }] }));
  const updateItem = (key, id, patch) => updateGate(key, (g) => ({ ...g, items: g.items.map((i) => (i.id === id ? { ...i, ...patch } : i)) }));
  const removeItem = (key, id) => updateGate(key, (g) => ({ ...g, items: g.items.filter((i) => i.id !== id) }));

  return (
    <div className="space-y-3" data-testid="process-gates-section">
      <SectionIntro title="Gates" badge={<LiveBadge what="Live · G1, G2, G4, G5 enforced" />}>
        Each gate is a hard stop on the bankruptcy route. <strong>System items</strong> are checks the portal performs itself (register search,
        debt threshold, service record…) — they can be switched off only with a logged reason. <strong>Attested items</strong>
        are boxes the handler ticks and signs; add as many as the process needs. The Head of Legal can override any open item with a reason,
        except items marked <em>statutory</em>.
      </SectionIntro>
      {gates.map((g) => (
        <div key={g.key} className="border rounded-md" data-testid={`gate-${g.key}`}>
          <div className="flex items-center justify-between px-3 py-2 bg-slate-50 border-b">
            <div>
              <span className="font-mono text-[10px] text-slate-500">{g.key}</span>
              {ENFORCED.includes(g.key)
                ? <span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded border bg-emerald-50 text-emerald-700 border-emerald-200">enforced</span>
                : <span className="ml-2 text-[9px] uppercase px-1.5 py-0.5 rounded border bg-amber-50 text-amber-800 border-amber-200">stored · later phase</span>}
              <div className="text-sm font-semibold text-slate-800">{g.label}</div>
              <div className="text-[10px] text-muted-foreground">{g.guards}</div>
            </div>
            <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => addItem(g.key)} data-testid={`gate-add-${g.key}`}>
              <Plus className="w-3 h-3 mr-1" /> Add attested item
            </Button>
          </div>
          <ul className="divide-y">
            {(g.items || []).map((it) => (
              <li key={it.id} className="flex items-center gap-2 px-3 py-1.5" data-testid={`gate-item-${it.id}`}>
                <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${it.kind === "system" ? "bg-indigo-50 text-indigo-700 border-indigo-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>{it.kind}</span>
                {it.kind === "system" && it.overridable === false && <span className="text-[9px] uppercase px-1.5 py-0.5 rounded border bg-slate-100 text-slate-600 border-slate-300" title="Statutory — not even the Head of Legal can override this">statutory</span>}
                <Input value={it.label} placeholder="What must be true / ticked before this gate opens" onChange={(e) => updateItem(g.key, it.id, { label: e.target.value })} className="h-7 text-xs flex-1" disabled={it.kind === "system"} data-testid={`gate-item-label-${it.id}`} />
                {it.kind === "system" && (
                  <>
                    <Switch checked={it.enabled !== false} onCheckedChange={(v) => updateItem(g.key, it.id, { enabled: v })} className="scale-75" data-testid={`gate-item-enabled-${it.id}`} />
                    {it.enabled === false && <Input value={it.disabled_reason || ""} placeholder="Reason for switching off (logged)" onChange={(e) => updateItem(g.key, it.id, { disabled_reason: e.target.value })} className="h-7 text-xs w-64" data-testid={`gate-item-reason-${it.id}`} />}
                  </>
                )}
                {it.kind !== "system" && (
                  <Button size="sm" variant="ghost" className="h-7 px-1.5 text-rose-600" onClick={() => removeItem(g.key, it.id)} data-testid={`gate-item-remove-${it.id}`}><Trash2 className="w-3 h-3" /></Button>
                )}
              </li>
            ))}
            {(g.items || []).length === 0 && <li className="px-3 py-2 text-[11px] italic text-muted-foreground">No checklist items yet.</li>}
          </ul>
        </div>
      ))}
    </div>
  );
}
